import { Dialog } from "primereact/dialog";
import React, { useRef, useState } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { InputNumber } from "primereact/inputnumber";
import SharedInputText from "../components/Shared/SharedInputText";
import SharedButton from "../components/Shared/SharedButton";
import { Toast } from "primereact/toast";
import supergitHIS from "../api/supergitHIS";
import toast from "react-hot-toast";
import validSupergitHIS from "../api/validSupergitHIS";

export default function StoreUpdate(props) {
  const {
    visible,
    setVisible,
    invoiceData,
    setInvoiceData,
    fetchAgain,
    setCancel = () => { },
    cancel = false,
  } = props;
  const { t } = useTranslation();
  const formikRef = useRef(null);
  const toastRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    return parseFloat(amount).toFixed(2);
  };

  const initialValues = {
    ClientName: invoiceData?.order?.ClientName || "",
    ClientCode: invoiceData?.order?.ClientCode || "",
    ClientOrderID: invoiceData?.order?.ClientOrderID || "",
    OrderDate: invoiceData?.order?.OrderDate || "",
    DeliveryDate: invoiceData?.order?.DeliveryDate || "",
    StoreName: invoiceData?.order?.StoreName || "",
    Address: invoiceData?.order?.Address || "",
    Phone: invoiceData?.order?.FirstPhone || invoiceData?.order?.Phone || "",
    SalesPersonName: invoiceData?.order?.SalesPersonName || "",
    TotalPrice: invoiceData?.order?.TotalPrice || 0,
    NetPrice: invoiceData?.order?.NetPrice || 0,
    UnpaidAmount: invoiceData?.order?.UnpaidAmount || 0,
    ClientBalance: invoiceData?.order?.ClientBalance || 0,
    InvoiceId: invoiceData?.order?.InvoiceId || "",
    LocalSerial: invoiceData?.order?.LocalSerial || "",
    Paid: invoiceData?.order?.Paid || false,
    Comment: invoiceData?.order?.Comment || "",
    items: invoiceData?.details ? [...invoiceData.details] : [],
    reason: "",
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const { items, ...rest } = values;
      const payload = {
        ...(!cancel
          ? {
            details: items,
          }
          : {
            details: items.map((item) => ({
              ...item,
              LocalSerial: item.LocalSerial,
              ClientName: item.ClientName,
              ProductCode: item.ProductCode,
              RevokeDate: item.OrderDate,
              Status: item.Status,
              Description: item.Description,
              ProductID: item.ProductID,
              Quantity: item.Quantity,
              CategoryID: item.CategoryID,
              ClientOrderDetailsID: item.ClientOrderDetailsID,
              ClientOrderID: item.ClientOrderID,
              ClientID: item.ClientID,
              NetPrice: item.NetPrice,
              TotalPrice: item.TotalPrice,
              StoreID: item.StoreID,
              Field1: "",
              StoreName: item.StoreName,
              ZatcaStatus: item.ZatcaStatus,
            })),
          }),

        ...(!cancel
          ? {
            order: {
              ...invoiceData?.order,
              ...rest,
              Status: Number(invoiceData?.order?.Status),
            },
          }
          : {
            revoke: {
              ...invoiceData?.order,
              ...rest,
              ClientName: invoiceData?.order?.ClientName,
              ClientID: invoiceData?.order?.ClientID,
              RevokeDate: invoiceData?.order?.OrderDate,
              Status: invoiceData?.order?.Status,
              StoreID: invoiceData?.order?.StoreID,
              LocalSerial: invoiceData?.order?.LocalSerial,
              TransYear: invoiceData?.order?.TransYear,
              Serial: invoiceData?.order?.Serial,
              ClientOrderID: invoiceData?.order?.ClientOrderID,
              RevokeAmount: invoiceData?.order?.NetPrice,
              EmployeeID: invoiceData?.order?.SalesPersonID,
              InvoiceId: invoiceData?.order?.InvoiceId,
              Notes: "",
              RevokeBy: invoiceData?.order?.SalesPersonName,
              RevokeReason: invoiceData?.order?.Comment || "",
              ClientPhone: invoiceData?.order?.Mobile,
              PaymentNotes: "",
              ClientAddress: invoiceData?.order?.Address,
              RevokeTime: new Date().toISOString(),
            },
          }),
      };
      const url = cancel
        ? `invoices/clientRevokes/edit/${invoiceData?.order?.ClientOrderID}`
        : `invoices/clientOrders/edit/${invoiceData?.order?.ClientOrderID}`;
      const response = await validSupergitHIS.post(url, payload);
      setInvoiceData(null);
      setVisible(false);
      setCancel(false);
      fetchAgain();
      toast.success(response?.data?.message);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        header={cancel ? t("Credit Note") : t("Edit Order")}
        visible={visible}
        onHide={() => {
          setVisible(false);
          setInvoiceData(null);
        }}
        pt={{
          root: { className: "w-full lg:w-10/12 mx-4 overflow-auto" },
          header: { className: "!p-2 !bg-darkBg !text-white" },
          content: { className: "!p-3" },
          closeButton: {
            className: "hover:!bg-black/20 !outline-none !shadow-none",
          },
          closeButtonIcon: { className: "!text-white" },
        }}
      >
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full mb-6">
                <SharedInputText
                  label={t("Client Name")}
                  name="ClientName"
                  value={formik.values.ClientName}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Order Date")}
                  name="OrderDate"
                  value={formatDate(formik.values.OrderDate)}
                  readOnly
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Delivery Date")}
                  name="DeliveryDate"
                  value={formatDate(formik.values.DeliveryDate)}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Store")}
                  name="StoreName"
                  value={formik.values.StoreName}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Address")}
                  name="Address"
                  value={formik.values.Address}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Phone")}
                  name="Phone"
                  value={formik.values.Phone}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Sales Person")}
                  name="SalesPersonName"
                  value={formik.values.SalesPersonName}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Total Price")}
                  name="TotalPrice"
                  value={`${formatCurrency(formik.values.TotalPrice)} SAR`}
                  readOnly
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Net Price")}
                  name="NetPrice"
                  value={`${formatCurrency(formik.values.NetPrice)} SAR`}
                  readOnly
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Unpaid Amount")}
                  name="UnpaidAmount"
                  value={`${formatCurrency(formik.values.UnpaidAmount)} SAR`}
                  readOnly
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Client Balance")}
                  name="ClientBalance"
                  value={`${formatCurrency(formik.values.ClientBalance)} SAR`}
                  readOnly
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Invoice ID")}
                  name="InvoiceId"
                  value={formik.values.InvoiceId}
                  readOnly
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Local Serial")}
                  name="LocalSerial"
                  value={formik.values.LocalSerial}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Status")}
                  name="Paid"
                  value={formik.values.Paid ? t("Paid") : t("Unpaid")}
                  readOnly
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t("Comment")}
                  name="Comment"
                  value={formik.values.Comment}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                {cancel && (
                  <SharedInputText
                    label={t("Reason")}
                    name="reason"
                    value={formik.values.reason}
                    onChange={formik.handleChange}
                    className="!p-1.5"
                  />
                )}
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4">
                  {t("Order Items")}
                </h3>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Code")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Description")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Qty")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Unit Price")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Discount (%)")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Client Discount (%)")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Total")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Discount Amount")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("After Discount")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Client Discount Amount")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Total Discount")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("Net Price")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {formik.values.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <SharedInputText
                              name={`items[${index}].ProductCode`}
                              value={item.ProductCode}
                              className="!p-1.5 !m-0 w-full"
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `items[${index}].ProductCode`,
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <SharedInputText
                              name={`items[${index}].Description`}
                              value={
                                item.Description || item.PrintInvoiceDescription
                              }
                              className="!p-1.5 !m-0 w-full"
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `items[${index}].Description`,
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={item.Quantity}
                              name={`items[${index}].Quantity`}
                              mode="decimal"
                              min={1}
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                              onValueChange={(e) =>
                                formik.setFieldValue(
                                  `items[${index}].Quantity`,
                                  e.value || 0
                                )
                              }
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={item.UnitPrice}
                              name={`items[${index}].UnitPrice`}
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                              onValueChange={(e) =>
                                formik.setFieldValue(
                                  `items[${index}].UnitPrice`,
                                  e.value || 0
                                )
                              }
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={item.Discount}
                              name={`items[${index}].Discount`}
                              mode="decimal"
                              min={0}
                              max={100}
                              suffix="%"
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                              onValueChange={(e) =>
                                formik.setFieldValue(
                                  `items[${index}].Discount`,
                                  e.value || 0
                                )
                              }
                            />
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={item.ClientDiscount}
                              name={`items[${index}].ClientDiscount`}
                              mode="decimal"
                              min={0}
                              max={100}
                              suffix="%"
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                              onValueChange={(e) =>
                                formik.setFieldValue(
                                  `items[${index}].ClientDiscount`,
                                  e.value || 0
                                )
                              }
                            />
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={(
                                (item.UnitPrice ?? 0) * (item.Quantity ?? 0)
                              ).toFixed(2)}
                              suffix=" SAR"
                              disabled
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                            />
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={(
                                ((item.UnitPrice ?? 0) *
                                  (item.Quantity ?? 0) *
                                  (item.Discount ?? 0)) /
                                100
                              ).toFixed(2)}
                              suffix=" SAR"
                              disabled
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                            />
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={(
                                (item.UnitPrice ?? 0) * (item.Quantity ?? 0) -
                                ((item.UnitPrice ?? 0) *
                                  (item.Quantity ?? 0) *
                                  (item.Discount ?? 0)) /
                                100
                              ).toFixed(2)}
                              suffix=" SAR"
                              disabled
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                            />
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={(
                                (((item.UnitPrice ?? 0) * (item.Quantity ?? 0) -
                                  ((item.UnitPrice ?? 0) *
                                    (item.Quantity ?? 0) *
                                    (item.Discount ?? 0)) /
                                  100) *
                                  (item.ClientDiscount ?? 0)) /
                                100
                              ).toFixed(2)}
                              suffix=" SAR"
                              disabled
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                            />
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={(
                                ((item.UnitPrice ?? 0) *
                                  (item.Quantity ?? 0) *
                                  (item.Discount ?? 0)) /
                                100 +
                                (((item.UnitPrice ?? 0) * (item.Quantity ?? 0) -
                                  ((item.UnitPrice ?? 0) *
                                    (item.Quantity ?? 0) *
                                    (item.Discount ?? 0)) /
                                  100) *
                                  (item.ClientDiscount ?? 0)) /
                                100
                              ).toFixed(2)}
                              suffix=" SAR"
                              disabled
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                            />
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={(
                                (item.UnitPrice ?? 0) * (item.Quantity ?? 0) -
                                (((item.UnitPrice ?? 0) *
                                  (item.Quantity ?? 0) *
                                  (item.Discount ?? 0)) /
                                  100 +
                                  (((item.UnitPrice ?? 0) *
                                    (item.Quantity ?? 0) -
                                    ((item.UnitPrice ?? 0) *
                                      (item.Quantity ?? 0) *
                                      (item.Discount ?? 0)) /
                                    100) *
                                    (item.ClientDiscount ?? 0)) /
                                  100)
                              ).toFixed(2)}
                              suffix=" SAR"
                              disabled
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-6 mt-6">
                <SharedButton
                  icon="pi pi-times"
                  label={t("Cancel")}
                  className="cancel-btn"
                  onClick={() => {
                    setVisible(false);
                    setInvoiceData(null);
                    setCancel(false);
                    formik.resetForm();
                  }}
                />
                <SharedButton
                  icon="pi pi-check"
                  label={t("Save")}
                  className="submit-btn"
                  onClick={formik.handleSubmit}
                  disabled={isSubmitting}
                />
              </div>
            </>
          )}
        </Formik>
      </Dialog>
    </>
  );
}
